import {
  Accessibility,
  Braces,
  Cpu,
  FlaskConical,
  KeyRound,
  Languages,
  Landmark,
  MessagesSquare,
  Palette,
  Radar,
  ScanSearch,
  ScrollText,
  ShieldCheck,
  UserCheck,
} from 'lucide-react';

import type { MedallionHue } from '@/components/bedrock';
import type { LucideIcon } from 'lucide-react';

/**
 * The four families the fourteen capabilities are grouped into, and the icon
 * each capability is scanned by.
 *
 * ## Why grouping, rather than a longer grid
 *
 * `/features` rendered all fourteen as peer cards in one four-column grid.
 * Three things were wrong with that and only one of them was mechanical:
 *
 *  - **It had no shape.** Fourteen white cards with a bold line and a grey
 *    paragraph is a specification, not a page. A reader arriving from a
 *    comparison has no way in and no way to stop — every card asks for the same
 *    attention, so none of them gets any.
 *  - **It read as a list of parts, to a reader shopping for outcomes.** The
 *    titles were the engineering nouns ("Drift detection", "Works with your
 *    IdP", "Verify-intent SDKs"). A non-technical buyer cannot assemble those
 *    into "what does this do for me"; a family heading can say it for them.
 *  - **Fourteen does not divide by four.** The last row held two cards and a
 *    ~700px hole, on every desktop width.
 *
 * Grouping fixes the third by construction rather than by arithmetic: a family
 * renders as a ROW LIST, and a list cannot leave a hole no matter how many
 * members it has. That is the point — the old defect could return the moment
 * somebody added a fifteenth capability, and this shape has no arrangement in
 * which it can.
 *
 * ## The hue is per family, not per card
 *
 * Fourteen differently-coloured icons is noise. One hue per family makes the
 * colour carry the grouping, so a reader who has scrolled past the heading can
 * still tell which family they are in.
 *
 * `feature-families.test.ts` pins that every SOT capability lands in exactly
 * one family — a capability added to `features.ts` and forgotten here would
 * otherwise simply not render, which is the failure that looks like nothing.
 */

export interface FeatureFamily {
  /** Stable id — the i18n namespace and the React key. */
  readonly id: string;
  readonly hue: MedallionHue;
  /** Slugs from the `FEATURES` SOT, in the order they should read. */
  readonly slugs: readonly string[];
}

export const FEATURE_FAMILIES: readonly FeatureFamily[] = [
  {
    id: 'reads',
    hue: 'cyan',
    slugs: ['auto-discovery', 'drift-detection', 'widget-plus-mcp', 'multi-provider'],
  },
  {
    id: 'acts',
    hue: 'blue',
    slugs: ['act-as-user', 'identity-providers', 'guarded-actions', 'audit-trail'],
  },
  {
    id: 'fits',
    hue: 'violet',
    slugs: ['design-studio', 'six-languages-rtl', 'wcag-and-bundle'],
  },
  {
    id: 'proves',
    hue: 'purple',
    slugs: ['eu-residency', 'eval-on-promote', 'sdks'],
  },
] as const;

/**
 * One icon per capability slug.
 *
 * Kept beside the families rather than on `FeatureCardData` because the SOT in
 * `features.ts` is content — titles, bodies and the numeric anchors the tests
 * assert on — and an icon is presentation. A page that wants the fourteen
 * capabilities as data should not have to import a React component set to do
 * it, which is what putting `LucideIcon` on the SOT would have required.
 */
export const FEATURE_ICONS: Readonly<Record<string, LucideIcon>> = {
  'auto-discovery': ScanSearch,
  'drift-detection': Radar,
  'widget-plus-mcp': MessagesSquare,
  'multi-provider': Cpu,
  'act-as-user': UserCheck,
  'identity-providers': KeyRound,
  'guarded-actions': ShieldCheck,
  'audit-trail': ScrollText,
  'design-studio': Palette,
  'six-languages-rtl': Languages,
  'wcag-and-bundle': Accessibility,
  'eu-residency': Landmark,
  'eval-on-promote': FlaskConical,
  sdks: Braces,
};
